import MarkdownRenderer from './MarkdownRenderer';
import { unitTransformers } from '../utils';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import type {
  LighthouseAudit,
  LighthouseDetails
} from '@/app/PageSpeed/api/types/analysisApiTypes';
import type { ComponentType, ReactElement } from 'react';

const SimpleURLRenderer = URLRenderer as (props: {
  url?: string | null;
}) => ReactElement;

type AgGridWrapperProps = {
  height?: string;
  columns?: unknown[];
  data?: unknown[];
  forceDir?: string;
  forceEnableRtl?: boolean;
  otherOptions?: Record<string, unknown>;
  settings?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

type Props = {
  audit: LighthouseAudit;
  analysisData?: LighthouseDetails;
};

const TableAuditRenderer = ({ audit, analysisData }: Props) => {
  const tableColumnConfig = audit?.details?.headings?.map((heading: any) => {
    let cellRenderer: ((params: any) => unknown) | null = null;
    const otherProps: Record<string, unknown> = {};
    switch (heading?.itemType || heading?.valueType) {
      case 'link':
      case 'url':
        otherProps.flex = 1;
        cellRenderer = (params: any) => {
          const value = params.value;
          return value ? (
            typeof value === 'string' ? (
              <SimpleURLRenderer url={value} />
            ) : (
              <SimpleURLRenderer url={value.url} />
            )
          ) : (
            ''
          );
        };
        break;
      case 'numeric':
        break;
      case 'bytes':
        cellRenderer = (params: any) => unitTransformers(params.value, 'bytes');
        break;
      case 'ms':
      case 'millisecond':
      case 'timespanMs':
        cellRenderer = (params: any) =>
          unitTransformers(params.value, 'millisecond');
        break;
      case 'node':
        otherProps.flex = 1;
        otherProps.autoHeight = true;
        cellRenderer = (params: any) => (
          <div>
            <span className="text-uppercase">{params.value?.nodeLabel}</span>

            <code
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                maxWidth: '100%'
              }}
            >
              {params.value?.snippet}
            </code>
          </div>
        );
        break;
      case 'source-location':
        cellRenderer = (params: any) => {
          const value = params.value;
          if (value && typeof value === 'object' && value.type && value.value) {
            return (
              <a
                href={`file://${value.value}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  display: 'block'
                }}
                title={value.value}
              >
                {value.value}
              </a>
            );
          }
          return value && typeof value === 'string' ? (
            <a
              href={`file://${value}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                display: 'block'
              }}
              title={value}
            >
              {value}
            </a>
          ) : (
            'N/A'
          );
        };
        break;

      case 'text':
        cellRenderer = (params: any) => (
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              maxWidth: '100%'
            }}
            title={params.value}
          >
            {params.value}
          </span>
        );
        break;
      case 'code':
        otherProps.flex = 1;
        cellRenderer = (params: any) => {
          const value = params.value;
          return value ? (
            typeof value === 'string' ? (
              <pre
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                  maxWidth: '100%'
                }}
                title={value}
              >
                {value}
              </pre>
            ) : (
              <pre
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                  maxWidth: '100%'
                }}
                title={value.value}
              >
                {value.value}
              </pre>
            )
          ) : (
            ''
          );
        };
        break;
      case 'thumbnail':
        cellRenderer = (params: any) => (
          <img
            src={params.value}
            alt="thumbnail"
            style={{ maxWidth: '100px' }}
          />
        );
        break;
      default:
        console.log(heading);
    }
    return {
      headerName: heading.text || heading.label,
      field: heading.key,
      cellRenderer,
      width: 200,
      ...otherProps
    };
  });

  const data = audit?.details?.items?.map((item: any) => {
    const rowData: Record<string, unknown> = {};
    audit?.details?.headings?.forEach((heading: any) => {
      rowData[heading.key] = item[heading.key];
    });
    return rowData;
  });

  const getStackPackDescription = (auditId?: string) => {
    if (!analysisData?.stackPacks || !auditId) return null;

    for (const pack of analysisData.stackPacks) {
      if (pack.descriptions && pack.descriptions[auditId]) {
        return {
          title: pack.title,
          iconDataURL: pack.iconDataURL,
          description: pack.descriptions[auditId]
        };
      }
    }
    return null;
  };
  const stackPackDescription = getStackPackDescription(audit.id);
  return (
    <div className="fs--1">
      <div className="mb-3">
        <MarkdownRenderer content={audit.description} />
      </div>
      {stackPackDescription && (
        <div className="stack-pack my-2 ">
          <div className="stack-pack-header">
            {stackPackDescription.iconDataURL && (
              <img
                src={stackPackDescription.iconDataURL}
                alt={`${stackPackDescription.title} icon`}
                style={{ width: '24px', height: '24px' }}
              />
            )}
            <span>{stackPackDescription.title}</span>
          </div>
          <MarkdownRenderer content={stackPackDescription.description} />
        </div>
      )}
      {audit?.details?.headings?.length > 0 &&
        audit?.details?.items?.length > 0 && (
          <TypedAgGridWrapper
            forceDir="ltr"
            forceEnableRtl={false}
            otherOptions={{ pagination: false }}
            columns={
              tableColumnConfig && tableColumnConfig.length > 0
                ? tableColumnConfig.some((col: { flex?: number }) => col.flex === 1)
                  ? tableColumnConfig
                  : [
                      { ...tableColumnConfig[0], flex: 1 },
                      ...tableColumnConfig.slice(1)
                    ]
                : []
            }
            data={data || []}
            height="300px"
          />
        )}
    </div>
  );
};

export default TableAuditRenderer;
